import { buildTree } from "@adafia/utils";
import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import type { FileNode } from "../../types";
import "./TreeView.css";
import { Button, Modal, Spinner } from "@adafia/ui";
import { useDeleteNode } from "@/notes/hooks/useDeleteNode";
import { useMoveNode } from "@/notes/hooks/useMoveNode";
import { useTree } from "@/notes/hooks/useTree";
import { useUpdateNote } from "@/notes/hooks/useUpdateNote";
import { TreeNode } from "./TreeNode";
import { TreeViewContext } from "./TreeViewContext";

export const TreeView: React.FC<{
	activeNote: FileNode | null;
	onSelect: (node: FileNode | null) => void;
	renamingId: string | null;
	setRenamingId: (id: string | null) => void;
}> = ({ activeNote, onSelect, renamingId, setRenamingId }) => {
	const { data: tree, isLoading, isError, error } = useTree();
	const { mutate: updateNote } = useUpdateNote();
	const { mutate: deleteNodeMutation } = useDeleteNode((id) => {
		if (activeNote && activeNote.id === id) {
			onSelect(null);
		}
	});
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
	const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
	const { mutate: moveNode } = useMoveNode();

	// Auto-expand parents of active note
	useEffect(() => {
		if (activeNote && tree) {
			setExpandedIds((prev) => {
				const next = new Set(prev);
				let currentId = activeNote.parentId;
				let changed = false;
				while (currentId) {
					if (!next.has(currentId)) {
						next.add(currentId);
						changed = true;
					}
					const parent = tree.find((n) => n.id === currentId);
					currentId = parent?.parentId || null;
				}
				return changed ? next : prev;
			});
		}
	}, [activeNote, tree]);

	const toggleExpanded = useCallback((id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const handleRenameNode = (
		id: string,
		newTitle: string,
		content?: string | null,
	) => {
		if (!id || !newTitle) return;
		updateNote({ id, title: newTitle, content });
	};

	const handleDeleteRequest = (id: string) => {
		setConfirmDeleteId(id);
	};

	const confirmDelete = () => {
		if (confirmDeleteId) {
			deleteNodeMutation(confirmDeleteId);
			setConfirmDeleteId(null);
		}
	};

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const nodeId = active.id as string;
		const newParentId = over.id as string;
		moveNode({
			id: nodeId,
			newParentId: newParentId === "root" ? null : newParentId,
		});
	};

	if (isLoading || !tree) {
		return (
			<div className="loading-container">
				<Spinner />
				<p>Loading notes...</p>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="error-container">
				<p>{error?.message}</p>
			</div>
		);
	}

	const builtTree = buildTree(tree);

	return (
		<DndContext onDragEnd={handleDragEnd}>
			<TreeViewContext.Provider
				value={{
					activeNote,
					renamingId,
					expandedIds,
					toggleExpanded,
					setRenamingId,
					onSelect,
					onRename: handleRenameNode,
					onDelete: handleDeleteRequest,
				}}
			>
				<div
					className="tree-view-container"
					onClick={() => onSelect(null)}
					onKeyDown={(e) => {
						if (e.key === "Escape") onSelect(null);
					}}
					tabIndex={0}
					role="tree"
					aria-label="Notes Tree"
				>
					{builtTree.map((rootNode) => (
						<TreeNode key={rootNode.id} node={rootNode} />
					))}
				</div>

				<Modal
					isOpen={!!confirmDeleteId}
					onClose={() => setConfirmDeleteId(null)}
					title="Confirm Delete"
				>
					<div className="delete-confirm-modal">
						<p>
							Are you sure you want to delete this item? This will permanently
							remove all nested files and folders.
						</p>
						<div
							className="modal-actions"
							style={{
								display: "flex",
								gap: "1rem",
								justifyContent: "flex-end",
								marginTop: "1.5rem",
							}}
						>
							<Button
								variant="secondary"
								onClick={() => setConfirmDeleteId(null)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								style={{ backgroundColor: "#ef4444" }}
								onClick={confirmDelete}
							>
								Delete
							</Button>
						</div>
					</div>
				</Modal>
			</TreeViewContext.Provider>
		</DndContext>
	);
};
